// Quick Count Setup - Full Inventory Count
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  Info,
  Calendar,
  MapPin,
  User,
  FileText,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { format } from "date-fns";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

export function StockTakesQuickCountSetupPage() {
  const { navigateTo } = useNavigation();
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    warehouse: "",
    assignedTo: "",
    description: `Quick Count - ${format(new Date(), "MMM dd, yyyy")}`,
    notes: "",
  });

  const handleNext = () => {
    // Validation - only date is required
    if (!formData.date) {
      toast.error("Please select a date");
      return;
    }

    // Navigate to count page with form data
    navigateTo("stock-takes/quick-count/count", { formData });
  };

  const handleSaveAndExit = () => {
    // Validation - only date is required
    if (!formData.date) {
      toast.error("Please select a date before saving");
      return;
    }

    try {
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference();
      
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: formData.description || `Quick Count - ${format(new Date(formData.date), "MMM dd, yyyy")}`,
        type: 'Quick Count',
        status: 'Draft',
        currentStep: 1,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User', // Would come from auth context
        setupData: {
          countDate: formData.date,
          warehouse: formData.warehouse,
          assignedTo: formData.assignedTo,
        },
      };
      
      saveStockTake(savedStockTake);
      toast.success("Stock take saved successfully! You can resume it later from the Stock Takes page.");
      navigateTo("stock-takes");
    } catch (error) {
      toast.error("Failed to save stock take. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateTo("stock-takes-home")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Quick Count</h1>
              <Badge variant="default">Full Inventory</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Set up a complete inventory count
            </p>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
              1
            </div>
            <span className="text-sm font-medium">Setup</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              2
            </div>
            <span className="text-sm text-muted-foreground">Count</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              3
            </div>
            <span className="text-sm text-muted-foreground">Review</span>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Quick Count includes all items
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                All items in the selected warehouse will be automatically included in this count.
                This is perfect for year-end audits, compliance requirements, or complete inventory verification.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Form */}
      <Card className="flex-1">
        <CardHeader>
          <CardTitle>Count Details</CardTitle>
          <CardDescription>
            Provide the basic information for this inventory count
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Count Date *
            </Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
            <p className="text-sm text-muted-foreground">
              When should this count be performed?
            </p>
          </div>

          {/* Warehouse */}
          <div className="space-y-2">
            <Label htmlFor="warehouse" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Warehouse
            </Label>
            <Select
              value={formData.warehouse}
              onValueChange={(value) => setFormData({ ...formData, warehouse: value })}
            >
              <SelectTrigger id="warehouse">
                <SelectValue placeholder="Select warehouse (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-warehouse">No Warehouse / All Inventory</SelectItem>
                {mockWarehouses.map((warehouse) => (
                  <SelectItem key={warehouse.code} value={warehouse.code}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {formData.warehouse === "no-warehouse" || !formData.warehouse
                ? "All inventory items will be counted"
                : "All items in this warehouse will be counted"}
            </p>
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assignedTo" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Assigned To
            </Label>
            <Select
              value={formData.assignedTo}
              onValueChange={(value) => setFormData({ ...formData, assignedTo: value })}
            >
              <SelectTrigger id="assignedTo">
                <SelectValue placeholder="Select person (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="john-smith">John Smith</SelectItem>
                <SelectItem value="jane-doe">Jane Doe</SelectItem>
                <SelectItem value="mike-johnson">Mike Johnson</SelectItem>
                <SelectItem value="sarah-williams">Sarah Williams</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Optional: Assign this count to a specific person
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description
            </Label>
            <Input
              id="description"
              placeholder="e.g., Year-End Inventory Count 2024"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <p className="text-sm text-muted-foreground">
              Optional: Add a description to identify this count
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any special instructions or notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes-home")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndExit}
          >
            <Save className="mr-2 h-4 w-4" />
            Save and Exit
          </Button>
          <Button onClick={handleNext}>
            Start Counting
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
